import { useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { BlockStack, InlineStack, Text, Card, Box, Divider, Banner } from '@shopify/polaris';
import { formatToCurrency } from '../../../shared/components/functions';

/**
 * Generate EPC QR code data for SEPA Credit Transfer
 * EPC QR code specification: https://www.europeanpaymentscouncil.eu/document-library/guidance-documents/quick-response-code-guidelines-enable-data-capture-initiation
 *
 * Format (each field separated by newline):
 * BCD - Service Tag
 * 002 - Version
 * 1 - Character Set (1 = UTF-8)
 * SCT - Identification Code
 * BIC (optional)
 * Beneficiary Name (max 70 chars)
 * IBAN
 * Amount (EUR format: EUR123.45)
 * Purpose Code (max 4 chars, optional)
 * Reference (max 35 chars, optional - structured)
 * Text (max 140 chars, optional - unstructured)
 * Information (optional)
 */
function generateEpcQrData({ beneficiaryName, iban, amount, currency = 'EUR', reference = '', remittanceInfo = '', bic = '' }) {
  // Clean IBAN - remove spaces
  const cleanIban = iban?.replace(/\s/g, '') || '';

  // Clean beneficiary name (max 70 chars)
  const cleanName = (beneficiaryName || '').substring(0, 70);

  // Format amount (must be in format like EUR123.45)
  const formattedAmount = amount ? `${currency}${parseFloat(amount).toFixed(2)}` : '';

  // Clean reference (max 35 chars) - used for structured reference like OGM
  const cleanReference = (reference || '').substring(0, 35);

  // Clean remittance info (max 140 chars) - unstructured text
  const cleanRemittanceInfo = (remittanceInfo || '').substring(0, 140);

  // Build EPC QR code string
  const lines = [
    'BCD', // Service Tag
    '002', // Version
    '1', // Character Set (UTF-8)
    'SCT', // Identification Code (SEPA Credit Transfer)
    bic || '', // BIC (optional)
    cleanName, // Beneficiary Name
    cleanIban, // IBAN
    formattedAmount, // Amount
    '', // Purpose Code (optional)
    cleanReference, // Reference (structured, optional)
    cleanRemittanceInfo, // Remittance Information (unstructured)
    '' // Information (optional)
  ];

  return lines.join('\n');
}

/**
 * Parse IBAN to display in formatted way
 */
function formatIban(iban) {
  if (!iban) return '';
  const clean = iban.replace(/\s/g, '');
  return clean.match(/.{1,4}/g)?.join(' ') || clean;
}

export default function EpcPaymentQrCode({ peppolPayload, invoiceNumber, openAmount }) {
  // Parse the Peppol payload
  const paymentData = useMemo(() => {
    if (!peppolPayload) return null;

    try {
      const data = typeof peppolPayload === 'string' ? JSON.parse(peppolPayload) : peppolPayload;

      // Extract payment information
      const paymentMeans = data.paymentMeans?.[0] || {};
      const seller = data.seller || {};
      const totals = data.totals || {};

      return {
        beneficiaryName: seller.name || '',
        iban: paymentMeans.iban || '',
        bic: paymentMeans.financialInstitutionBranch || '',
        amount: openAmount || totals.payableAmount || totals.taxInclusiveAmount || '',
        currency: data.currency || 'EUR',
        invoiceNumber: data.invoiceNumber || invoiceNumber || '',
        reference: paymentMeans.reference || data.invoiceNumber || '',
        dueDate: data.dueDate || '',
        seller: {
          name: seller.name || '',
          street: seller.street || '',
          street2: seller.street2 || '',
          postalZone: seller.postalZone || '',
          city: seller.city || '',
          country: seller.country || '',
          vatNumber: seller.vatNumber || ''
        },
        buyer: data.buyer || {},
        totals: {
          taxExclusiveAmount: totals.taxExclusiveAmount || '',
          taxInclusiveAmount: totals.taxInclusiveAmount || '',
          payableAmount: totals.payableAmount || ''
        }
      };
    } catch (e) {
      console.error('Error parsing PeppolJSON:', e);
      return null;
    }
  }, [peppolPayload, openAmount, invoiceNumber]);

  // Generate the EPC QR code data
  const qrData = useMemo(() => {
    if (!paymentData || !paymentData.iban) return null;

    return generateEpcQrData({
      beneficiaryName: paymentData.beneficiaryName,
      iban: paymentData.iban,
      amount: paymentData.amount,
      currency: paymentData.currency,
      reference: paymentData.reference,
      remittanceInfo: `Invoice ${paymentData.invoiceNumber}`,
      bic: paymentData.bic
    });
  }, [paymentData]);

  if (!paymentData) {
    return null;
  }

  if (!paymentData.iban) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h3">
            📱 Payment Details
          </Text>
          <Banner tone="warning">
            <Text>No IBAN available in the Peppol data. Manual payment details entry required.</Text>
          </Banner>
        </BlockStack>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h3">
          📱 Scan to Pay
        </Text>

        {/* QR Code - centered and compact */}
        <Box>
          <InlineStack align="center">
            <div
              style={{
                padding: '12px',
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #e1e3e5'
              }}
            >
              <QRCodeSVG value={qrData} size={140} level="M" includeMargin={false} />
            </div>
          </InlineStack>
          <Box paddingBlockStart="200">
            <Text variant="bodySm" tone="subdued" alignment="center">
              Scan with your banking app
            </Text>
          </Box>
        </Box>

        <Divider />

        {/* Payment Details */}
        <BlockStack gap="200">
          <Text variant="headingSm" as="h4">
            Payment Details
          </Text>

          <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: '2px 8px', fontSize: '13px' }}>
            <Text variant="bodySm" tone="subdued">
              To:
            </Text>
            <Text variant="bodySm" fontWeight="semibold">
              {paymentData.beneficiaryName}
            </Text>

            <Text variant="bodySm" tone="subdued">
              IBAN:
            </Text>
            <Text variant="bodySm" fontWeight="semibold">
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{formatIban(paymentData.iban)}</span>
            </Text>

            {paymentData.bic && (
              <>
                <Text variant="bodySm" tone="subdued">
                  BIC:
                </Text>
                <Text variant="bodySm">{paymentData.bic}</Text>
              </>
            )}

            <Text variant="bodySm" tone="subdued">
              Amount:
            </Text>
            <Text variant="bodyMd" fontWeight="bold" tone="success">
              {formatToCurrency(paymentData.amount)}
            </Text>

            <Text variant="bodySm" tone="subdued">
              Reference:
            </Text>
            <Text variant="bodySm">{paymentData.reference}</Text>

            {paymentData.dueDate && (
              <>
                <Text variant="bodySm" tone="subdued">
                  Due:
                </Text>
                <Text variant="bodySm">{paymentData.dueDate}</Text>
              </>
            )}
          </div>
        </BlockStack>

        <Divider />

        {/* Supplier Address */}
        <BlockStack gap="100">
          <Text variant="bodySm" tone="subdued">
            Supplier
          </Text>
          <Text variant="bodySm">
            {paymentData.seller.street}
            {paymentData.seller.street2 && `, ${paymentData.seller.street2}`}, {paymentData.seller.postalZone} {paymentData.seller.city}
            {paymentData.seller.vatNumber && ` • VAT: ${paymentData.seller.vatNumber}`}
          </Text>
        </BlockStack>

        <Banner tone="info">
          <Text variant="bodySm">Scan the QR code with your banking app to auto-fill payment details.</Text>
        </Banner>
      </BlockStack>
    </Card>
  );
}
